import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import { arSA, enUS } from '@clerk/localizations';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { LangProvider, useLang } from './lib/i18n.js';
import { clerkAppearance } from './lib/clerkAppearance.js';
import './index.css';
import './styles/dash.css';

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;
if (!clerkKey) {
  console.warn('VITE_CLERK_PUBLISHABLE_KEY missing — auth will fail.');
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

// Clerk's localization must follow the active language, so we read it from
// LangProvider (which sits above this) and pass arSA / enUS accordingly.
function ClerkWithLang({ children }: { children: React.ReactNode }) {
  const { lang } = useLang();
  return (
    <ClerkProvider
      publishableKey={clerkKey}
      afterSignOutUrl="/sign-in"
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/onboarding"
      appearance={clerkAppearance}
      localization={lang === 'ar' ? arSA : enUS}
    >
      {children}
    </ClerkProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <LangProvider>
      <ClerkWithLang>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </ClerkWithLang>
    </LangProvider>
  </React.StrictMode>,
);
